import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Star, MapPin, Clock, Users, Loader2, Send, Heart, ChevronRight, Navigation, PartyPopper, ChevronDown, ChevronUp } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useCourses } from "@/hooks/useCourses";
import { useCourseImages } from "@/hooks/useCourseImages";
import { useCourseProducts } from "@/hooks/useCourseProducts";
import { useAuth } from "@/hooks/useAuth";
import { CourseImageCarousel } from "@/components/CourseImageCarousel";
import { LazyProductRequestModal } from "@/components/LazyModals";
import TrailViewer from "@/components/TrailViewer";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackCourseView } from "@/lib/activity-tracker";

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    transportation: false,
    events: false,
  });
  
  const { data: courses, isLoading, error } = useCourses();
  const { data: courseImages, isLoading: imagesLoading } = useCourseImages(id || "");
  const { data: courseProducts, isLoading: productsLoading } = useCourseProducts(id || "");

  const course = courses?.find(c => c.course_id === id);
  
  // 상품이 있는지만 확인 (trips 여부와 관계없이)
  const hasAvailableProducts = courseProducts && courseProducts.length > 0;

  // 페이지 로드 시 스크롤을 맨 위로 이동 및 코스 조회 추적
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // 코스 조회 추적
    if (course?.course_id && course?.name) {
      trackCourseView(course.course_id, course.name);
    }
  }, [course]);

  const getDifficultyLevel = (difficulty: string | null): number => {
    if (!difficulty) return 2;
    switch (difficulty) {
      case "쉬움":
        return 1;
      case "보통":
        return 2;
      case "어려움":
        return 3;
      default:
        return 2;
    }
  };

  const renderDifficultyStars = (level: number) => {
    return Array.from({ length: 3 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < level ? "fill-[#F39800] text-[#F39800]" : "text-gray-300"
        }`}
      />
    ));
  };

  const formatDuration = (minutes: number | null): string => {
    if (!minutes) return "정보 없음";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
    }
    return `${mins}분`;
  };

  const formatDistance = (km: number | null): string => {
    if (km === null || km === undefined) return "정보 없음";
    return `${km}km`;
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const parseTravelerInfo = (travelerInfo: string) => {
    const lines = travelerInfo.split("\n");
    const transportation = [];
    const events = [];

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      // 교통 정보 패턴 매칭
      if (
        trimmedLine.includes("시점:") ||
        trimmedLine.includes("종점:") ||
        trimmedLine.includes("교통편)") ||
        trimmedLine.includes("버스") ||
        trimmedLine.includes("정류장") ||
        trimmedLine.includes("터미널")
      ) {
        transportation.push(trimmedLine);
      }
      // 축제/이벤트 정보 패턴 매칭
      else if (
        trimmedLine.includes("축제") ||
        trimmedLine.includes("음악회") ||
        trimmedLine.includes("해넘이") ||
        trimmedLine.includes("해맞이") ||
        trimmedLine.includes("10월") ||
        trimmedLine.includes("5월") ||
        trimmedLine.includes("연말연시")
      ) {
        events.push(trimmedLine);
      }
    }

    return { transportation, events };
  };

  const formatDescription = (text: string): string => {
    if (!text) return "";
    
    // 이미 \n이 있으면 그대로 사용
    if (text.includes('\n')) {
      return text;
    }
    
    // \n이 없으면 - 기준으로 줄바꿈 (단, 문장의 시작에 있는 -만)
    return text.replace(/\s*-\s*/g, '\n- ').trim();
  };

  const getImageUrl = (): string => {
    if (course?.main_photo_url) {
      return course.main_photo_url;
    }
    // 기본 이미지 URL들
    const defaultImages = [
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2000&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1615729947596-a598e5de0ab3?q=80&w=2000&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1439886183900-e79ec0057170?q=80&w=2000&auto=format&fit=crop",
    ];
    return defaultImages[parseInt(course?.course_id.slice(-1) || "0") % defaultImages.length];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#F39800]" />
          <span className="ml-2 text-gray-600">코스 정보를 불러오는 중...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="text-center py-20">
          <p className="text-red-600 mb-2">코스 정보를 불러오는 데 실패했습니다.</p>
          <p className="text-gray-600">{error.message}</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="text-center py-20">
          <p className="text-gray-600 text-lg">해당 코스를 찾을 수 없습니다.</p>
          <Link 
            to="/courses" 
            className="inline-block mt-4 bg-[#F39800] hover:bg-[#E08700] text-white px-6 py-3 rounded-lg transition-colors"
          >
            코스 목록으로 돌아가기
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header />
      
      {/* Breadcrumbs - Enhanced visual design */}
      <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center flex-wrap gap-2" aria-label="브레드크럼">
            {/* Home */}
            <Link 
              to="/" 
              className="group flex items-center text-sm transition-all duration-200"
              aria-label="홈으로 이동"
            >
              <div className="flex items-center px-3 py-1.5 rounded-full bg-white border border-gray-200 hover:border-[#F39800] hover:bg-[#F39800]/5 transition-all duration-200">
                <span className="text-gray-600 group-hover:text-[#F39800] font-medium">홈</span>
              </div>
            </Link>
            
            <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
            
            {/* 코스 찾기 */}
            <Link 
              to="/courses" 
              className="group flex items-center text-sm transition-all duration-200"
              aria-label="코스 찾기로 이동"
            >
              <div className="flex items-center px-3 py-1.5 rounded-full bg-white border border-gray-200 hover:border-[#F39800] hover:bg-[#F39800]/5 transition-all duration-200">
                <span className="text-gray-600 group-hover:text-[#F39800] font-medium">코스 찾기</span>
              </div>
            </Link>
            
            <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
            
            {/* Trail Name - 코스 리스트로 이동 가능 */}
            <Link 
              to={`/courses?trail=${encodeURIComponent(course.trail_name)}#course-results`}
              className="group flex items-center text-sm transition-all duration-200"
              aria-label={`${course.trail_name} 코스 목록으로 이동`}
            >
              <div className="flex items-center px-3 py-1.5 rounded-full bg-[#F39800]/10 border border-[#F39800]/20 hover:border-[#F39800] hover:bg-[#F39800]/20 transition-all duration-200">
                <MapPin className="w-3.5 h-3.5 text-[#F39800] mr-1.5" />
                <span className="text-[#F39800] font-medium">{course.trail_name}</span>
              </div>
            </Link>
            
            <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
            
            {/* Current Course */}
            <div className="flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r from-[#F39800]/20 to-[#F39800]/10 border border-[#F39800]/30">
              <span className="text-gray-800 font-semibold text-sm">{course.name}</span>
            </div>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Desktop/Tablet Layout - 2 Column Grid with synchronized scrolling */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
          
          {/* Left Column - Image and Main Info (2/3 width on desktop) */}
          <div className="md:col-span-2 space-y-6">
            {/* Course Image Carousel */}
            <div className="relative">
              {courseImages && courseImages.length > 0 ? (
                <CourseImageCarousel
                  images={courseImages}
                  courseName={course.name}
                  className="w-full rounded-2xl shadow-lg overflow-hidden aspect-[16/10]"
                />
              ) : (
                <div className="w-full aspect-[16/10] rounded-2xl shadow-lg overflow-hidden">
                  {imagesLoading ? (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <div className="flex items-center space-x-2 text-gray-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>이미지를 불러오는 중...</span>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={getImageUrl()}
                      alt={course.name}
                      className="w-full h-full object-cover"
                      data-testid="course-image"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Course Header - Only shown on mobile */}
            {isMobile && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <Badge variant="secondary" className="bg-[#F39800]/10 text-[#F39800] px-3 py-1 text-sm font-medium" data-testid="trail-name">
                    {course.trail_name}
                  </Badge>
                  <div className="flex items-center bg-gray-50 rounded-full px-3 py-1">
                    <span className="text-sm text-gray-600 mr-2">난이도:</span>
                    <div className="flex items-center">
                      {renderDifficultyStars(getDifficultyLevel(course.difficulty))}
                      <span className="text-sm text-gray-700 ml-2 font-medium" data-testid="difficulty-label">{course.difficulty || "정보 없음"}</span>
                    </div>
                  </div>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 mb-3 leading-tight" data-testid="course-title">
                  {course.name}
                </h1>

                <p className="text-gray-600 leading-relaxed text-base whitespace-pre-line" data-testid="course-short-description">
                  {formatDescription(course.short_desc || course.description || "코스 설명이 없습니다.")}
                </p>
              </div>
            )}

            {/* Desktop Course Header */}
            {!isMobile && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <Badge variant="secondary" className="bg-[#F39800]/10 text-[#F39800] px-3 py-1 text-sm font-medium" data-testid="trail-name">
                    {course.trail_name}
                  </Badge>
                  <div className="flex items-center bg-gray-50 rounded-full px-3 py-1">
                    <span className="text-sm text-gray-600 mr-2">난이도:</span>
                    <div className="flex items-center">
                      {renderDifficultyStars(getDifficultyLevel(course.difficulty))}
                      <span className="text-sm text-gray-700 ml-2 font-medium" data-testid="difficulty-label">{course.difficulty || "정보 없음"}</span>
                    </div>
                  </div>
                </div>

                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight" data-testid="course-title">
                  {course.name}
                </h1>

                <p className="text-gray-600 leading-relaxed text-lg whitespace-pre-line" data-testid="course-short-description">
                  {formatDescription(course.short_desc || course.description || "코스 설명이 없습니다.")}
                </p>
              </div>
            )}
          </div>

          {/* Right Column - Quick Stats & Info (1/3 width on desktop) */}
          {!isMobile && (
            <div className="space-y-6">
              {/* Quick Stats Card - No longer sticky for synchronized scrolling */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">코스 정보</h3>
                
                <div className="space-y-4">
                  {/* Distance */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <span className="text-gray-600 font-medium">총 거리</span>
                    <span className="text-xl font-bold text-[#F39800]" data-testid="stats-distance">
                      {formatDistance(course.length)}
                    </span>
                  </div>
                  
                  {/* Duration */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <span className="text-gray-600 font-medium">소요시간</span>
                    <span className="text-xl font-bold text-[#F39800]" data-testid="stats-duration">
                      {formatDuration(course.estimated_time)}
                    </span>
                  </div>
                  
                  {/* Difficulty */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <span className="text-gray-600 font-medium">난이도</span>
                    <span className="text-lg font-bold text-gray-800" data-testid="stats-difficulty">
                      {course.difficulty || "보통"}
                    </span>
                  </div>
                  
                  {/* Start Point */}
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <MapPin className="w-4 h-4 text-[#F39800] mr-2" />
                      <span className="text-gray-600 font-medium">출발지</span>
                    </div>
                    <div className="text-gray-800 font-medium">
                      {course.start_point || "출발지 정보 없음"}
                    </div>
                  </div>
                </div>
                
                {/* CTA Button */}
                {productsLoading ? (
                  <Button 
                    disabled
                    className="w-full mt-6 bg-gray-300 text-gray-500 py-3 text-lg font-semibold rounded-xl"
                    size="lg"
                  >
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    상품 정보 확인 중...
                  </Button>
                ) : hasAvailableProducts ? (
                  <Button 
                    onClick={() => navigate(`/product-detail/${course.course_id}`)}
                    className="w-full mt-6 bg-[#F39800] hover:bg-[#E08700] text-white py-3 text-lg font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                    size="lg"
                  >
                    이 코스로 여행 예약하기
                  </Button>
                ) : user ? (
                  <div className="mt-6 space-y-3">
                    <Button 
                      onClick={() => setIsRequestModalOpen(true)}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 text-lg font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                      size="lg"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      투어 상품 요청하기
                    </Button>
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                      <p className="text-sm text-blue-700 text-center">
                        💡 아직 이 코스의 트레킹 여행이 준비되지 않았어요<br />
                        홀씨지기가 직접 안내하는 트레킹 투어를 원하신다면 요청해주세요!
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 space-y-3">
                    <Button 
                      onClick={() => navigate('/auth')}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 text-lg font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                      size="lg"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      투어 상품 요청하기
                    </Button>
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                      <p className="text-sm text-blue-700 text-center">
                        💡 아직 이 코스의 트레킹 여행이 준비되지 않았어요<br />
                        홀씨지기가 직접 안내하는 트레킹 투어를 원하신다면 요청해주세요!
                      </p>
                    </div>
                    <p className="text-sm text-gray-500 text-center">
                      로그인 후 투어 상품을 요청할 수 있습니다
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Content Section */}
        <div className="mt-12 space-y-8">
          
          {/* Interactive Trail Map */}
          <div className="space-y-6">
            <TrailViewer 
              courseId={course.course_id}
              courseData={{
                estimated_time: course.estimated_time,
                name: course.name,
                difficulty: course.difficulty
              }}
            />
          </div>
          
          {/* Route Information */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <MapPin className="w-5 h-5 text-[#F39800] mr-2" />
              경로 정보
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                <div className="text-sm text-green-600 font-medium mb-1">출발지</div>
                <div className="text-gray-900 font-semibold text-lg" data-testid="route-start-point">
                  {course.start_point || "정보 없음"}
                </div>
              </div>
              <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                <div className="text-sm text-blue-600 font-medium mb-1">도착지</div>
                <div className="text-gray-900 font-semibold text-lg" data-testid="route-end-point">
                  {course.end_point || "정보 없음"}
                </div>
              </div>
            </div>
          </div>

          {/* Description and Info Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Course Description */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">코스 설명</h3>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line" data-testid="course-description">
                  {formatDescription(course.description || course.short_desc || "코스 설명이 없습니다.")}
                </p>
              </div>
            </div>

            {/* Tour Info */}
            {course.tour_info && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">투어 정보</h3>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {formatDescription(course.tour_info)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Transportation and Events Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Transportation Info Section */}
            {course.traveler_info && parseTravelerInfo(course.traveler_info).transportation.length > 0 && (
              <Collapsible
                open={expandedSections.transportation}
                onOpenChange={() => toggleSection("transportation")}
              >
                <Card className="shadow-sm border border-gray-100">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-4">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Navigation className="w-5 h-5 text-[#F39800]" />
                          교통 정보
                        </CardTitle>
                        {expandedSections.transportation ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                        {parseTravelerInfo(
                          course.traveler_info
                        ).transportation.map(
                          (line: string, index: number) => (
                            <div
                              key={index}
                              className="flex items-start gap-2"
                            >
                              <div className="w-2 h-2 bg-[#F39800] rounded-full mt-2 flex-shrink-0"></div>
                              <p className="text-sm text-gray-700">
                                {line.replace(/^-\s*/, "")}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Events Info Section */}
            {course.traveler_info && parseTravelerInfo(course.traveler_info).events.length > 0 && (
              <Collapsible
                open={expandedSections.events}
                onOpenChange={() => toggleSection("events")}
              >
                <Card className="shadow-sm border border-gray-100">
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-4">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <PartyPopper className="w-5 h-5 text-[#F39800]" />
                          축제 및 이벤트
                        </CardTitle>
                        {expandedSections.events ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                        {parseTravelerInfo(course.traveler_info).events.map(
                          (line: string, index: number) => (
                            <div
                              key={index}
                              className="flex items-start gap-2"
                            >
                              <PartyPopper className="w-4 h-4 text-[#F39800] flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-gray-700">
                                {line.replace(/^-\s*/, "")}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}
          </div>

          {/* Final CTA Section */}
          {hasAvailableProducts ? (
            <div className="bg-gradient-to-r from-[#F39800] to-[#E08700] rounded-2xl p-8 text-center text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-3">준비되셨나요?</h3>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
                도심 속에서 자연의 여유를 만끽하고 싶다면, {course.name}로 떠나보세요! 잊지 못할 추억을 만들 수 있을 것입니다.
              </p>
              <Button 
                onClick={() => navigate(`/product-detail/${course.course_id}`)}
                className="bg-white text-[#F39800] hover:bg-gray-100 hover:text-[#F39800] font-bold py-3 px-8 text-lg rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                size="lg"
              >
                지금 바로 예약하기
              </Button>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-center text-white shadow-lg">
              <h3 className="text-2xl font-bold mb-3">홀씨지기와 함께 걷고 싶으신가요? 🌱</h3>
              <p className="text-lg opacity-90 mb-2 max-w-2xl mx-auto">
                아직 이 코스는 홀씨지기가 준비 중이에요!
              </p>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
                이 멋진 둘레길을 홀씨지기와 함께 걷고 싶으시다면, 투어 상품 개발을 요청해주세요.<br />
                여러분의 관심이 새로운 여행의 시작이 됩니다!
              </p>
              {user ? (
                <Button 
                  onClick={() => setIsRequestModalOpen(true)}
                  className="bg-white text-blue-600 hover:bg-gray-100 font-bold py-3 px-8 text-lg rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                  size="lg"
                >
                  <Send className="w-4 h-4 mr-2" />
                  투어 상품 요청하기
                </Button>
              ) : (
                <Button 
                  onClick={() => navigate('/auth')}
                  className="bg-white text-blue-600 hover:bg-gray-100 hover:text-blue-600 font-bold py-3 px-8 text-lg rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
                  size="lg"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  로그인하고 요청하기
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile Sticky Bottom Bar */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t shadow-lg z-50">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="secondary" className="bg-[#F39800]/10 text-[#F39800] text-xs">
                    {course.trail_name}
                  </Badge>
                  <span className="text-xs text-gray-600">{formatDistance(course.length)}</span>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">{course.name}</h3>
              </div>
              {productsLoading ? (
                <Button 
                  disabled
                  className="bg-gray-300 text-gray-500 px-6 py-2.5 text-sm font-semibold rounded-lg"
                >
                  <Loader2 className="w-4 h-4 animate-spin" />
                </Button>
              ) : hasAvailableProducts ? (
                <Button 
                  onClick={() => navigate(`/product-detail/${course.course_id}`)}
                  className="bg-[#F39800] hover:bg-[#E08700] text-white px-6 py-2.5 text-sm font-semibold rounded-lg shadow-md"
                >
                  예약하기
                </Button>
              ) : user ? (
                <div className="flex flex-col items-end">
                  <Button 
                    onClick={() => setIsRequestModalOpen(true)}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2.5 text-sm font-semibold rounded-lg shadow-md"
                  >
                    요청하기
                  </Button>
                  <p className="text-xs text-gray-500 mt-1 text-right">
                    홀씨지기와 함께 떠나는<br />트레킹을 원하신다면?
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-end">
                  <Button 
                    onClick={() => navigate('/auth')}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2.5 text-sm font-semibold rounded-lg shadow-md"
                  >
                    로그인
                  </Button>
                  <p className="text-xs text-gray-500 mt-1 text-right">
                    홀씨지기와 함께 떠나는<br />트레킹을 원하신다면?
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Add padding bottom on mobile to account for sticky bar + safe area */}
      <div className={isMobile ? "pb-mobile-nav" : ""}>
        <Footer />
      </div>
      
      {/* Product Request Modal */}
      {course && (
        <LazyProductRequestModal
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          courseId={course.course_id}
          courseName={course.name}
          trailName={course.trail_name}
        />
      )}
    </div>
  );
};

export default CourseDetail;